import { useEffect, useState } from 'react';
import { api, publicApi, loadLocal, saveLocal } from './api';

const PREFIX = 'perfect-slice.public.v2:';
const CURRENT = PREFIX + 'current';
const memory = new Map();
const pending = new Map();
let challengeCheckedAt = 0;
export const utcDate = () => new Date().toISOString().slice(0, 10);
export const playerIdentity = (user, guest) => user?.alias_confirmed ? user.id : guest?.id ? `guest:${guest.id}` : 'guest';

export function useUtcDate() {
  const [day, setDay] = useState(utcDate);
  useEffect(() => {
    let timer;
    const check = () => {
      setDay(utcDate());
      clearTimeout(timer);
      const now = new Date();
      timer = setTimeout(check, Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()+1)-now.getTime()+25);
    };
    check();
    document.addEventListener('visibilitychange', check);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', check); };
  }, []);
  return day;
}

const read = key => memory.get(key) || loadLocal(key);
const write = (key, value) => { if (value) memory.set(key, value); else memory.delete(key); saveLocal(key, value); };
function summary(value) {
  if (!value || typeof value.id !== 'string' || !/^[a-f0-9-]{36}$/i.test(value.id) || value.date !== utcDate() || !Number.isInteger(value.number) || !['preview', 'production'].includes(value.scope)) return null;
  return { id: value.id, date: value.date, number: value.number, scope: value.scope, resets_at: value.resets_at };
}
export function sanitizeChallenge(value) {
  const base = summary(value);
  if (!base || !Array.isArray(value.geometry) || value.geometry.length < 3 || value.geometry.length > 2048 || !value.geometry.every(p => Array.isArray(p) && p.length === 2 && p.every(n => typeof n === 'number' && Number.isFinite(n))) || !Number.isInteger(value.target) || value.target < 20 || value.target > 80 || !Number.isInteger(value.colorIndex) || value.colorIndex < 0 || value.colorIndex > 4 || typeof value.generator_version !== 'string' || !Number.isFinite(value.rotation)) return null;
  return { ...base, target: value.target, geometry: value.geometry.map(p => [...p]), family: value.family, variant: value.variant, rotation: value.rotation, colorIndex: value.colorIndex, generator_version: value.generator_version };
}
export function sanitizeBoard(value) {
  const challenge = summary(value?.challenge);
  if (!challenge || !Array.isArray(value.entries) || value.entries.length > 50 || !Number.isInteger(value.total_players)) return null;
  const entries = value.entries.map(row => ({ rank: row.rank, player: row.player, score: row.score, error: row.error }));
  if (!entries.every(row => Number.isInteger(row.rank) && row.rank > 0 && typeof row.player === 'string' && row.player.length <= 80 && Number.isFinite(row.score) && Number.isFinite(row.error))) return null;
  // Explicit whitelist: NEVER persist my_entry, is_you, user, auth or attempt state.
  return { challenge, entries, total_players: value.total_players };
}
function selectChallenge(challenge) {
  const suffix = `${challenge.date}:${challenge.id}`;
  const keys = new Set(memory.keys());
  try { for (let i = 0; i < localStorage.length; i++) keys.add(localStorage.key(i)); } catch { /* Memory-only cache. */ }
  for (const key of keys) if (key?.startsWith(PREFIX) && key !== CURRENT && !key.endsWith(suffix)) write(key, null);
  write(CURRENT, summary(challenge));
}
function current() {
  const item = read(CURRENT);
  if (summary(item)) return item;
  if (item) {
    for (const kind of ['challenge', 'board']) write(`${PREFIX}${kind}:${item.date}:${item.id}`, null);
    write(CURRENT, null);
  }
  return null;
}
export function cachedChallenge() {
  const item = current();
  return item ? sanitizeChallenge(read(`${PREFIX}challenge:${item.date}:${item.id}`)) : null;
}
export function cacheChallenge(value) {
  const challenge = sanitizeChallenge(value);
  if (!challenge) throw new Error('Today’s shape changed. Please reconnect for the current Daily Slice.');
  selectChallenge(challenge);
  write(`${PREFIX}challenge:${challenge.date}:${challenge.id}`, challenge);
  return challenge;
}
export function cachedBoard() {
  const item = current();
  return item ? sanitizeBoard(read(`${PREFIX}board:${item.date}:${item.id}`)) : null;
}
export function cacheBoard(value) {
  const board = sanitizeBoard(value);
  if (!board) throw new Error('Today’s leaderboard changed. Please try again.');
  selectChallenge(board.challenge);
  write(`${PREFIX}board:${board.challenge.date}:${board.challenge.id}`, board);
  return board;
}
function singleFlight(key, action) {
  if (!pending.has(key)) pending.set(key, Promise.resolve().then(action).finally(() => pending.delete(key)));
  return pending.get(key);
}
export function fetchChallenge(force = false) {
  const cached = cachedChallenge();
  // Deduplicate landing prefetch, StrictMode and immediate route transitions.
  if (!force && cached && Date.now()-challengeCheckedAt < 2000) return Promise.resolve(cached);
  return singleFlight('challenge:' + utcDate(), async () => {
    const response = await publicApi('/daily/challenge');
    const challenge = cacheChallenge(response.challenge);
    challengeCheckedAt = Date.now();
    return challenge;
  });
}
export const fetchBoard = () => singleFlight('board:' + utcDate(), async () => cacheBoard(await publicApi('/daily/leaderboard/public')));
export const fetchDailyState = identity => singleFlight(`state:${utcDate()}:${identity}`, () => api('/daily/state', { timeoutMs: 12000 }));
